import { Middleware } from "@reduxjs/toolkit";

import { isApiOffline, setApiOffline } from "./connectionState";

/** Retry every 10 s while the server can't be reached. */
export const RETRY_MS = 10_000;
/** Most queries remembered for the refetch when the server is back. */
const MAX_REMEMBERED = 50;

type QueryArg = {
  endpointName?: string;
  originalArgs?: unknown;
  type?: string;
};
type ApiLike = {
  reducerPath: string;
  endpoints: Record<string, unknown>;
};

/** "workSlice/executeQuery/rejected" -> ["workSlice", "rejected"]. */
export const queryResult = (type: unknown) => {
  if (typeof type !== "string") return null;
  const match = /^(.+)\/executeQuery\/(fulfilled|rejected)$/.exec(type);
  return match ? { reducerPath: match[1], result: match[2] } : null;
};

/** A fetchBaseQuery network failure (no answer at all), not an HTTP error. */
export const isNetworkFailure = (payload: unknown) =>
  (payload as { status?: unknown } | undefined)?.status === "FETCH_ERROR";

/**
 * Notices when API calls fail because the server can't be reached: shows the
 * "Can't reach ProCharted — retrying…" banner, retries one of the failed
 * queries every 10 s, and when an answer arrives again re-runs every query
 * that failed meanwhile (before, a page stayed empty until another page was
 * opened). QA batch 16, NAV-026.
 */
export const createConnectionMiddleware = (apis: ApiLike[]): Middleware => {
  const byPath = new Map(apis.map((api) => [api.reducerPath, api]));
  const failed = new Map<string, { reducerPath: string; arg: QueryArg }>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  return (store) => {
    const rerun = (entry: { reducerPath: string; arg: QueryArg }) => {
      const api = byPath.get(entry.reducerPath);
      const endpoint = entry.arg.endpointName
        ? (api?.endpoints[entry.arg.endpointName] as
            | {
                initiate?: (
                  arg: unknown,
                  options: { forceRefetch: boolean; subscribe: boolean },
                ) => unknown;
              }
            | undefined)
        : undefined;
      if (!endpoint?.initiate) return;
      store.dispatch(
        endpoint.initiate(entry.arg.originalArgs, {
          forceRefetch: true,
          subscribe: false,
        }) as never,
      );
    };

    const scheduleRetry = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        const first = failed.values().next().value;
        if (first && isApiOffline()) rerun(first);
      }, RETRY_MS);
    };

    return (next) => (action) => {
      const result = next(action);
      const parsed = queryResult((action as { type?: unknown }).type);
      if (!parsed || !byPath.has(parsed.reducerPath)) return result;
      const meta = (action as { meta?: { arg?: QueryArg } }).meta;
      const arg = meta?.arg;
      if (!arg?.endpointName) return result;
      let key = `${parsed.reducerPath}|${arg.endpointName}|`;
      try {
        key += JSON.stringify(arg.originalArgs ?? null);
      } catch {
        key += String(arg.originalArgs);
      }

      if (parsed.result === "rejected") {
        if (isNetworkFailure((action as { payload?: unknown }).payload)) {
          if (failed.size < MAX_REMEMBERED || failed.has(key)) {
            failed.set(key, { reducerPath: parsed.reducerPath, arg });
          }
          setApiOffline(true);
          scheduleRetry();
        }
        return result;
      }

      // An answer arrived: the server is reachable.
      failed.delete(key);
      if (isApiOffline()) {
        setApiOffline(false);
        const waiting = Array.from(failed.values());
        failed.clear();
        waiting.forEach(rerun);
      }
      return result;
    };
  };
};
